/**
 * 🧶 **PAINTING A RIGGED CREATURE — the fleece tinted, the bare skin left alone.**
 *
 * 🎯 **haugur, 2026-09-24, on the first rigged flock:** *"color is off, they are
 * the same brown colour from head to toe, not very realistic like that."*
 *
 * 🔴 **The cause was one multiply.** The model is generated *"a creamy white
 * fleece, a bare pale face, pale hooves"*, so **the albedo already tells the
 * parts apart** — and tinting the whole body threw that away.
 *
 * ✅ **The rig knows where the bare parts are**, so the split needs no second
 * texture, no second material and no hand-painted mask.
 *
 * 🔴 **THIS FILE EXISTS SO THE GAME AND THE SAMPLE SHEET CANNOT DISAGREE.**
 * ⚠️ *A sample sheet is a claim about the game, and it differs from the game in
 * exactly the places nobody thought about* — that cost two bugs on the water,
 * and the sheet had already drifted once on this very feature.
 */

import { BufferAttribute, Color } from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { attribute, mix, texture, uniform, uv, vec3 } from 'three/tsl';
import { bareBones, type QuadAnatomy, type RigBone } from './quadrig.ts';

/** What a painted body reports back, for an F8 report. */
export interface Painted {
  /** 0–1: what share of the vertices are fleece rather than bare skin */
  readonly fleeceShare: number;
  /** false when the mesh carried no skin weights at all */
  readonly split: boolean;
}

/**
 * 🧶 Work out how much of each vertex is fleece and write it on the geometry.
 *
 * 🔴 **`fleece = 1 − (the weight on bare bones)`, as a SUM.** ✅ A sum makes the
 * join between a bare leg and the wool above it a gradient across the vertices
 * the two bones share. ⚠️ **Taking the single strongest bone instead draws a
 * hard colour edge**, which reads as a seam — and being rid of seams is the
 * whole reason this animal has a skeleton.
 */
export function markFleece(
  skinned: unknown,
  anatomy: QuadAnatomy,
  bones: readonly RigBone[],
): Painted {
  const geom = (
    skinned as {
      geometry: {
        attributes: Record<string, { array: ArrayLike<number>; count: number } | undefined>;
        setAttribute: (n: string, a: unknown) => void;
      };
    }
  ).geometry;
  const joints = geom.attributes.skinIndex;
  const weights = geom.attributes.skinWeight;
  const count = geom.attributes.position?.count ?? 0;
  const bare = bareBones(bones, anatomy);
  const fleece = new Float32Array(count);

  if (!joints || !weights) {
    /* ⚠️ No skin weights would tint the whole body, which is the fault this
       replaces. Say so rather than quietly going back to one colour. */
    fleece.fill(1);
    geom.setAttribute('fleece', new BufferAttribute(fleece, 1));
    return { fleeceShare: 1, split: false };
  }

  let total = 0;
  for (let v = 0; v < count; v++) {
    let bareness = 0;
    for (let k = 0; k < 4; k++) {
      const j = joints.array[v * 4 + k] ?? 0;
      const w = weights.array[v * 4 + k] ?? 0;
      if (bare.has(j)) bareness += w;
    }
    const f = Math.max(0, Math.min(1, 1 - bareness));
    // eslint-disable-next-line security/detect-object-injection -- a loop counter into a typed array.
    fleece[v] = f;
    total += f;
  }
  geom.setAttribute('fleece', new BufferAttribute(fleece, 1));
  return { fleeceShare: Number((total / Math.max(1, count)).toFixed(3)), split: true };
}

/**
 * 🎨 **The creature's one material — and it is the one the game already
 * compiles.**
 *
 * 🔴 **`MeshLambertNodeMaterial` with a `colorNode`, and nothing else.**
 * ⚠️ **A new material path blacked out dev AND staging once**, and a skinned
 * mesh needs no new one: `NodeMaterial.setupPosition` applies `skinning()` to any
 * `SkinnedMesh` by itself.
 */
/**
 * 🩸 **The red a struck creature flashes — D125, 2026-09-28.** In place of the
 * fleece and bare-skin tints, for {@link creatureMaterial}'s red twin.
 * 🔴 **Here, not in `sheepmesh.ts`, so `rigsheet.html` bakes the same red the
 * field draws.** ⚠️ Dials.
 */
export const HURT_FLEECE = uniform(new Color(0xc0402e));
export const HURT_BARE = uniform(new Color(0xff5a50));

/**
 * 💙 **The blue a sheep flashes when it eats the turf, and pulses while it is
 * ready to mate** — D130. 🎯 haugur: *"make it flash blue once if grass hits
 * it"*, then *"flash and pulse"*. The red twin's shape, the other colour.
 * ⚠️ Dials.
 */
export const READY_FLEECE = uniform(new Color(0x3f6fc4));
export const READY_BARE = uniform(new Color(0x8fb4ff));

export function creatureMaterial(
  map: unknown,
  fleeceTint: ReturnType<typeof uniform>,
  bareTint: ReturnType<typeof uniform>,
  daylight: ReturnType<typeof uniform>,
): MeshLambertNodeMaterial {
  const material = new MeshLambertNodeMaterial();
  if (map === null || map === undefined) {
    /* ⚠️ No texture would draw BLACK and nothing would warn. */
    material.colorNode = uniform(new Color(0x9a8b74));
    return material;
  }
  const tile = texture(map as never, uv());
  /* 🧶 The tint is chosen PER VERTEX: fleece where the wool is, bare skin on
     the face, the ears and the legs. */
  const tint = mix(bareTint, fleeceTint, attribute('fleece'));
  material.colorNode = vec3(tile.r, tile.g, tile.b).mul(tint).mul(daylight.mul(0.35).add(0.65));
  return material;
}
